import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { IntegrationQueueViewModel } from "../../Types/ExportTypes";
import { ListQuery } from "../../Types/CommonTypes";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import IntegrationSystemAutoComplete from "../AutoComplete/Integration/IntegrationSystemAutoComplete";
import ResponsivePagination from "../ResponsivePagination";
interface OrderIntegrationsComponentProps {
    documentNo: string;
    onCountChange?: (count: number) => void;
}

const booleanFilterProperties = ["ext_Handled", "ext_Notified", "ext_Locked"];

const defaultQuery: ListQuery = {
    page: 0,
    pageSize: 10,
    filter: "",
    filters: [],
};

const OrderIntegrationsComponent: React.FC<OrderIntegrationsComponentProps> = ({ documentNo, onCountChange }) => {
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [items, setItems] = useState<IntegrationQueueViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [query, setQuery] = useState<ListQuery>(() => ({ ...defaultQuery, filters: [] }));
    const [filterProperty, setFilterProperty] = useState("");
    const [filter, setFilter] = useState("");
    const [deletingId, setDeletingId] = useState<number | null>(null);

    useEffect(() => {
        setQuery({ ...defaultQuery, filters: [] });
    }, [documentNo]);

    useEffect(() => {
        if (!documentNo) {
            setItems([]);
            setTotalCount(0);
            onCountChange?.(0);
            return;
        }

        const fetchIntegrations = async () => {
            showLoading();
            try {
                const response = await authService.post<{ items: IntegrationQueueViewModel[]; count: number }>(
                    `/integrationApi/GetIntegrationsForOrder?documentNo=${encodeURIComponent(documentNo)}`,
                    query,
                );
                const fetchedItems = response.items ?? [];
                const fetchedCount = response.count ?? 0;
                setItems(fetchedItems);
                setTotalCount(fetchedCount);
                onCountChange?.(fetchedCount);
            } catch (error: any) {
                showSnackbar(`${t("common.failedToFetchData")} : ${error.response?.data ?? error.message}`, "error");
            } finally {
                hideLoading();
            }
        };

        fetchIntegrations();
    }, [authService, documentNo, query, showLoading, hideLoading, showSnackbar, t, onCountChange]);

    const isBooleanProperty = useMemo(
        () => booleanFilterProperties.includes(filterProperty),
        [filterProperty],
    );


    const isSystemProperty = useMemo(
        () => filterProperty === "ext_SystemId",
        [filterProperty],
    );
    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery((prev) => {
            let property = filterProperty;
            if (!property?.trim() && filter?.trim()) {
                property = "Default";
            }

            if (property?.trim() && filter?.trim()) {
                const newFilter = buildQueryFilter(property, filter);
                const isDuplicate = prev.filters?.some(
                    (f) => f.property === newFilter.property && f.value === newFilter.value,
                );
                if (isDuplicate) {
                    return prev;
                }

                return {
                    ...prev,
                    filters: [...(prev.filters ?? []), newFilter],
                    page: 0,
                    filter: "",
                };
            }

            return {
                ...prev,
                page: 0,
                filter,
            };
        });

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        if (isLoading) {
            return;
        }
        setQuery((prev) => ({ ...prev, page: newPage }));
    };

    const handleDelete = async (item: IntegrationQueueViewModel) => {
        if (!item.id) return;
        if (!window.confirm(t("dialog.areYouSure"))) return;

        setDeletingId(item.id);
        showLoading();
        try {
            await authService.post("/integrationApi/DeleteIntegrationQueueItem", {
                id: item.id,
                key: item.key,
            });
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            setQuery((prev) => {
                const newTotal = Math.max(totalCount - 1, 0);
                const totalPages = Math.ceil(newTotal / prev.pageSize);
                const targetPage = prev.page >= totalPages && prev.page > 0 ? Math.max(totalPages - 1, 0) : prev.page;
                return { ...prev, page: targetPage };
            });
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setDeletingId(null);
            hideLoading();
        }
    };

    const formatDateTime = (value?: string | null) => {
        if (!value) return "";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    };

    const filterOptions = [
        { value: "", label: t("common.default") },
        { value: "ext_Key", label: t("common.key") },
        { value: "ext_Message", label: t("common.message") },
        { value: "ext_SystemId", label: t("common.integration") },
        { value: "ext_EventType", label: t("common.type") },
        { value: "ext_Handled", label: t("common.handled") },
        { value: "ext_Notified", label: t("common.notified") },
        { value: "ext_Locked", label: t("common.locked") },
    ];

    return (
        <Paper sx={{ padding: 2 }}>
            <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        >
                            {filterOptions.map((option) => (
                         
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        {isSystemProperty ? (
                            <IntegrationSystemAutoComplete
                                onChange={(o) => setFilter(o?.value || "")}
                            />
                        ) : isBooleanProperty ? (
                            <TextField
                                select
                                label={t("common.value")}
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                SelectProps={{ native: true }}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                disabled={isLoading}
                            >
                                <option value="true">{t("common.yes")}</option>
                                <option value="false">{t("common.no")}</option>
                            </TextField>
                        ) : (
                            <TextField
                                label={t("common.search")}
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={isLoading}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
                        <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading}>
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {query.filters?.map((f, index) => (
                    <Chip
                        key={`${f.property}-${f.value}-${index}`}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() =>
                            setQuery((prev) => ({
                                ...prev,
                                filters: prev.filters?.filter((_, i) => i !== index),
                                page: 0,
                            }))
                        }
                    />
                ))}
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                {items.map((item) => {
                    const isHandled = item.handled === true;
                    const isNotified = item.notified === true;
                    const isLocked = item.locked === true;

                    return (
                        <Grid item xs={12} key={item.id ?? `${item.key}-${item.created}`}>
                            <Card
                                variant="outlined"
                                sx={(theme) => ({
                                    backgroundColor: !isHandled
                                        ? alpha(theme.palette.warning.light, 0.18)
                                        : theme.palette.background.paper,
                                    borderLeft: `6px solid ${!isHandled ? theme.palette.warning.main : theme.palette.divider
                                        }`,
                                })}
                            >
                                <CardHeader
                                    titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
                                    title={item.systemName || t("common.integration")}
                                    subheader={
                                        item.createdWhen || item.created ? formatDateTime(item.createdWhen ?? item.created) : undefined
                                    }
                                />
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "150px 1fr" },
                                            rowGap: 0.5,
                                            columnGap: 2,
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {t("common.key")}:
                                        </Typography>
                                        <Typography variant="body2">{item.key || ""}</Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            {t("common.type")}:
                                        </Typography>
                                        <Typography variant="body2">{item.eventType || ""}</Typography>
                                        {item.itemNo && (
                                            <>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("common.itemNo")}:
                                                </Typography>

                                                <Typography variant="body2">{item.itemNo || ""}</Typography>
                                            </>
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            {t("common.error")}:
                                        </Typography>
                                        <Typography variant="body2">{formatDateTime(item.lastError) || t("common.none")}</Typography>
                                    </Box>

                                    {item.message && (
                                        <Box sx={{ mt: 1.5 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {t("common.message")}:
                                            </Typography>
                                            <Box
                                                component="pre"
                                                sx={{
                                                    m: 0,
                                                    p: 1,
                                                    borderRadius: 1,
                                                    border: "1px dashed",
                                                    borderColor: "divider",
                                                    whiteSpace: "pre-wrap",
                                                    fontFamily:
                                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                    fontSize: 13,
                                                    maxHeight: 180,
                                                    overflow: "auto",
                                                }}
                                            >
                                                {item.message}
                                            </Box>
                                        </Box>
                                    )}

                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                                        <Chip
                                            color={isHandled ? "success" : "default"}
                                            variant={isHandled ? "filled" : "outlined"}
                                            label={`${t("common.handled")}: ${isHandled ? t("common.yes") : t("common.no")}`}
                                            title={formatDateTime(item.handledWhen) || undefined}
                                        />
                                        <Chip
                                            color={isNotified ? "info" : "default"}
                                            variant={isNotified ? "filled" : "outlined"}
                                            label={`${t("common.notified")}: ${isNotified ? t("common.yes") : t("common.no")}`}
                                            title={formatDateTime(item.notifiedWhen) || undefined}
                                        />
                                        <Chip
                                            color={isLocked ? "warning" : "default"}
                                            variant={isLocked ? "filled" : "outlined"}
                                            label={`${t("common.locked")}: ${isLocked ? t("common.yes") : t("common.no")}`}
                                        />
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDelete(item)}
                                        disabled={isLoading || deletingId === item.id}
                                    >
                                        {t("common.delete")}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}

                {!isLoading && items.length === 0 && (
                    <Grid item xs={12}>
                        <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                )}
            </Grid>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                sx={{ mt: 2 }}
            />
        </Paper>
    );
};

export default OrderIntegrationsComponent;